import { htmlSafe } from '@ember/template';

export enum ImportType {
  Workspace = 'workspace',
  WorkspaceAndSegment = 'workspace_and_segment',
  Object = 'object',
}

export enum SqlImportDatabase {
  mysql = 'mysql',
  postgres = 'postgres',
  snowflake = 'snowflake',
  bigQuery = 'bigquery',
  redshift = 'redshift',
  sqlserver = 'sqlserver',
}

export function getSqlDatabaseImageSrc(type: SqlImportDatabase) {
  return {
    [SqlImportDatabase.mysql]: new URL('cio/assets/images/integrations/mysql.svg', import.meta.url).toString(),
    [SqlImportDatabase.postgres]: new URL('cio/assets/images/integrations/postgresql.svg', import.meta.url).toString(),
    [SqlImportDatabase.snowflake]: new URL('cio/assets/images/integrations/snowflake.svg', import.meta.url).toString(),
    [SqlImportDatabase.bigQuery]: new URL('cio/assets/images/integrations/bigquery.svg', import.meta.url).toString(),
    [SqlImportDatabase.redshift]: new URL('cio/assets/images/integrations/redshift.svg', import.meta.url).toString(),
    [SqlImportDatabase.sqlserver]: new URL('cio/assets/images/integrations/sqlserver.svg', import.meta.url).toString(),
  }[type];
}

export function getSqlDatabaseName(type: SqlImportDatabase) {
  return {
    [SqlImportDatabase.mysql]: 'MySQL',
    [SqlImportDatabase.postgres]: 'PostgreSQL',
    [SqlImportDatabase.snowflake]: 'Snowflake',
    [SqlImportDatabase.bigQuery]: 'BigQuery',
    [SqlImportDatabase.redshift]: 'Amazon Redshift',
    [SqlImportDatabase.sqlserver]: 'Microsoft SQL Server',
  }[type];
}

export function getSqlDatabaseDocumentationUrl(type: SqlImportDatabase) {
  return {
    [SqlImportDatabase.mysql]: 'https://customer.io/docs/sql-sync/',
    [SqlImportDatabase.postgres]: 'https://customer.io/docs/postgres-sync/',
    [SqlImportDatabase.snowflake]: 'https://customer.io/docs/snowflake-sync/',
    [SqlImportDatabase.bigQuery]: 'https://customer.io/docs/bigquery-sync/',
    [SqlImportDatabase.redshift]: 'https://customer.io/docs/redshift-sync/',
    [SqlImportDatabase.sqlserver]: 'https://customer.io/docs/ms-sql-sync/',
  }[type];
}

export enum SqlSourceTypes {
  Database = 'database',
  DataWarehouse = 'data warehouse',
}

export function getSqlStorageType(type: SqlImportDatabase): SqlSourceTypes {
  return {
    [SqlImportDatabase.mysql]: SqlSourceTypes.Database,
    [SqlImportDatabase.postgres]: SqlSourceTypes.Database,
    [SqlImportDatabase.snowflake]: SqlSourceTypes.DataWarehouse,
    [SqlImportDatabase.bigQuery]: SqlSourceTypes.DataWarehouse,
    [SqlImportDatabase.sqlserver]: SqlSourceTypes.Database,
    [SqlImportDatabase.redshift]: SqlSourceTypes.DataWarehouse,
  }[type];
}

export enum SqlImportsCreateStep {
  Details = 'details',
  Settings = 'settings',
  Database = 'database',
  Query = 'query',
  RelationshipsQuery = 'relationshipsQuery',
  Review = 'review',
}

export type SqlImportPreviewErrors = 'id_not_present' | 'query_not_parseable' | 'forbidden_query';
export type SqlImportPreviewWarnings =
  | 'select_star_query'
  | 'limit_not_present'
  | 'sensitive_fields_found'
  | 'not_a_select_query';

export type SqlImportPreviewResponse = {
  result: Partial<{
    columns: string[];
    rows: string[][];
  }> | null;
  validations: Partial<{
    errors: SqlImportPreviewErrors[];
    warnings: SqlImportPreviewWarnings[];
  }> | null;
  error: string;
};

export const SQL_IMPORT_ERRORS = {
  'bigquery: constructing client': 'Key file contents must be a valid JSON object.',
  'no such host': `We could not connect to your database host. Check that your host name is correct and try again.`,
  'connection refused': `We could not connect to your database on the port you provided. Check that your port is correct and try again.`,
  'access denied for user': `Access denied for user. Please check your connection credentials and try again.`,
  'password authentication failed for user': `Access denied for user. Please check your connection credentials and try again.`,
  'no route to host': `Your host name could not be resolved. Check that your host name and port are correct and try again.`,
  'unknown database': `Your database could not be found. Check that your database name is correct and try again.`,
  'database.*does not exist': `Your database could not be found. Check that your database name is correct and try again.`,
  "table.*doesn't exist": `The table you're trying to query from doesn't exist. Please check your table name and try again.`,
  'SSL off': `We could not connect to your database host. Please check your SSL connection settings and try again.`,
  eof: 'Your query returned no rows.',
};

/* eslint-disable spellcheck/spell-checker */
export const BIG_QUERY_LOCATIONS = [
  {
    groupName: 'Multi-regional',
    options: [
      { label: 'US', value: 'US' },
      { label: 'EU', value: 'EU' },
    ],
  },
  {
    groupName: 'Americas',
    options: [
      { label: 'Iowa (us-central1)', value: 'us-central1' },
      { label: 'Las Vegas (us-west4)', value: 'us-west4' },
      { label: 'Los Angeles (us-west2)', value: 'us-west2' },
      { label: 'Montréal (northamerica-northeast1)', value: 'northamerica-northeast1' },
      { label: 'Northern Virginia (us-east4)', value: 'us-east4' },
      { label: 'Oregon (us-west1)', value: 'us-west1' },
      { label: 'Salt Lake City (us-west3)', value: 'us-west3' },
      { label: 'São Paulo (southamerica-east1)', value: 'southamerica-east1' },
      { label: 'Santiago (southamerica-west1)', value: 'southamerica-west1' },
      { label: 'South Carolina (us-east1)', value: 'us-east1' },
      { label: 'Toronto (northamerica-northeast2)', value: 'northamerica-northeast2' },
    ],
  },
  {
    groupName: 'Europe',
    options: [
      { label: 'Belgium (europe-west1)', value: 'europe-west1' },
      { label: 'Finland (europe-north1)', value: 'europe-north1' },
      { label: 'Frankfurt (europe-west3)', value: 'europe-west3' },
      { label: 'London (europe-west2)', value: 'europe-west2' },
      { label: 'Netherlands (europe-west4)', value: 'europe-west4' },
      { label: 'Warsaw (europe-central2)', value: 'europe-central2' },
      { label: 'Zürich (europe-west6)', value: 'europe-west6' },
    ],
  },
  {
    groupName: 'Asia Pacific',
    options: [
      { label: 'Delhi (asia-south2)', value: 'asia-south2' },
      { label: 'Hong (Kong asia-east2)', value: 'Kong asia-east2' },
      { label: 'Jakarta (asia-southeast2)', value: 'asia-southeast2' },
      { label: 'Melbourne (australia-southeast2)', value: 'australia-southeast2' },
      { label: 'Mumbai (asia-south1)', value: 'asia-south1' },
      { label: 'Osaka (asia-northeast2)', value: 'asia-northeast2' },
      { label: 'Seoul (asia-northeast3)', value: 'asia-northeast3' },
      { label: 'Singapore (asia-southeast1)', value: 'asia-southeast1' },
      { label: 'Sydney (australia-southeast1)', value: 'australia-southeast1' },
      { label: 'Taiwan (asia-east1)', value: 'asia-east1' },
      { label: 'Tokyo (asia-northeast1)', value: 'asia-northeast1' },
    ],
  },
];
/* eslint-enable spellcheck/spell-checker */

// eslint-disable-next-line spellcheck/spell-checker
const RPC_ERROR_PREFIX = 'rpc error: code = Unknown desc = ';
// eslint-disable-next-line spellcheck/spell-checker
const BIG_QUERY_ERROR_PREFIX = 'googleapi: Error 400: ';

export function transformError(error: string) {
  let key = Object.keys(SQL_IMPORT_ERRORS).find((phrase) => {
    let pattern = new RegExp(phrase, 'gi');
    return error.match(pattern);
  }) as keyof typeof SQL_IMPORT_ERRORS | undefined;

  if (!key) {
    return error.replace(RPC_ERROR_PREFIX, '').replace(BIG_QUERY_ERROR_PREFIX, '');
  }

  return htmlSafe(SQL_IMPORT_ERRORS[key]);
}

export function isQueryTimedOutError(error: string) {
  // same as hard-coded API error message:
  return error === 'The query timed out.';
}

export function isDatabase(type: SqlImportDatabase) {
  return [SqlImportDatabase.mysql, SqlImportDatabase.postgres].includes(type);
}

export function isDataWarehouse(type?: SqlImportDatabase) {
  if (type == null) {
    return false;
  }

  return [SqlImportDatabase.snowflake, SqlImportDatabase.bigQuery].includes(type);
}
